
import { z } from "zod";

const getAge = (dob: Date) => {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

  if (!hasBirthdayPassed) {
    age -= 1;
  }

  return age;
};

const getAgeRangeLabel = (ageFrom: number, ageTo: number) =>
  ageTo === 99 ? `${ageFrom} years old and above` : `${ageFrom} to ${ageTo} years old`;

const hasAgeRestriction = (ageFrom: number, ageTo: number) =>
  ageFrom !== 0 || ageTo !== 0;

export const formatNameInput = (value: string) =>
  value
    .replace(/[^A-Za-z\s]/g, "")
    .replace(/\s{2,}/g, " ")
    .slice(0, 50)
    .toLowerCase()
    .replace(/\b[a-z]/g, (char) => char.toUpperCase());

const passengerNameSchema = (fieldLabel: string) => z
  .string()
  .transform(formatNameInput)
  .pipe(
    z
      .string()
      .trim()
      .min(1, `${fieldLabel} is required`)
      .max(50, `${fieldLabel} must not exceed 50 characters`)
      .regex(
        /^[A-Za-z]+(?: [A-Za-z]+)*$/,
        `${fieldLabel} can only contain letters and spaces`,
      ),
  );

export const passengerDetailsSchema = z.object({
  paymentTicketId: z.string().min(1),
  ageFrom: z.number(),
  ageTo: z.number(),
  firstName: passengerNameSchema("First name"),
  lastName: passengerNameSchema("Last name"),
  gender: z.string().min(1, "Gender is required"),
  nationality: z.string().min(1, "Nationality is required"),
  dob: z
    .date()
    .optional()
    .refine((value) => value !== undefined, {
      message: "Date of birth is required",
    }),
  identityNumber: z
    .string()
    .min(1, "MYKAD/Passport No. is required"),
})
  .superRefine((data, ctx) => {
    if (data.dob && hasAgeRestriction(data.ageFrom, data.ageTo)) {
      const age = getAge(data.dob);

      if (age < data.ageFrom || age > data.ageTo) {
        ctx.addIssue({
          code: "custom",
          path: ["dob"],
          message: `Age must be within the range ${getAgeRangeLabel(data.ageFrom, data.ageTo)}`,
        });
      }
    }

    const identityNumber = data.identityNumber.trim().toUpperCase();
    const nationality =
      data.nationality === "MYS" ||
      data.nationality === "Malaysia"
        ? "Malaysia"
        : data.nationality;

    const myKadRegex = /^\d{6}-\d{2}-\d{4}$/;
    const passportRegex = /^[A-Z0-9]{6,15}$/;

    if (nationality === "Malaysia") {
      // Malaysia → MYKAD OR Passport
      if (
        !myKadRegex.test(identityNumber) &&
        !passportRegex.test(identityNumber)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["identityNumber"],
          message: "Enter a valid MYKAD or Passport number.",
        });

        return;
      }

      // Only check DOB for MYKAD
      if (myKadRegex.test(identityNumber) && data.dob) {
        const icDob = identityNumber.slice(0, 6);

        const year = String(data.dob.getFullYear()).slice(-2);
        const month = String(data.dob.getMonth() + 1).padStart(2, "0");
        const day = String(data.dob.getDate()).padStart(2, "0");

        const dobFromDate = `${year}${month}${day}`;

        if (icDob !== dobFromDate) {
          ctx.addIssue({
            code: "custom",
            path: ["identityNumber"],
            message: "MYKAD number does not match the date of birth.",
          });
        }
      }
    } else {
      // Non-Malaysia → Passport ONLY
      if (!passportRegex.test(identityNumber)) {
        ctx.addIssue({
          code: "custom",
          path: ["identityNumber"],
          message: "Enter a valid Passport number.",
        });
      }
    }
  });

export type PassengerDetailsForm = z.infer<
  typeof passengerDetailsSchema
>;